import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Checkbox,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DoubleScrollbar from 'react-double-scrollbar';
import FilterViewButton from './FilterViewButton';
import { useLocalization } from 'src/context/LocalizationContext';

const hoverColor = '#f5f5f5';

const hoverStyles = {
  '&:hover': {
    backgroundColor: hoverColor,
    cursor: 'pointer',
  },
};

const DynamicTable = ({
  data,
  headers,
  rowsPerPageOptions = [25, 50, 100],
  initialRowsPerPage = 25,
  initialOrder,
  initalOrderDirection,
  isOrdered,
  rowOnClick,
  selected,
  handleCheckboxChange,
  handleModal,
  isMobile,
  children,
  rowStyles,
  resetPage,
  showCheckbox = false,
  rightHeaderSlot,
  leftHeaderSlot,
  bordered = false,
}) => {
  const { language } = useLocalization();

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isCompactHeader = useMediaQuery(theme.breakpoints.down('md'));

  const [order, setOrder] = useState(initalOrderDirection || 'desc');
  const [orderBy, setOrderBy] = useState(initialOrder || '');
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [sortBy, setSortBy] = useState(undefined);
  const [page, setPage] = useState(0);

  const columns = useMemo(() => {
    const base = Array.isArray(headers) ? headers : [];
    if (!isOrdered) return base;

    const hasNo = base.some((c) => c.id === 'no');
    if (hasNo) return base;

    return [
      { id: 'no', label: 'No', alignRight: false, sx: { width: 10 } },
      ...base,
    ];
  }, [headers, isOrdered]);

  useEffect(() => {
    if (resetPage) setPage(0);
  }, [resetPage]);

  const handleRequestSort = useCallback(
    (property, sortKeyFromHeader) => {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
      setSortBy(sortKeyFromHeader || property);
      setPage(0);
    },
    [order, orderBy]
  );

  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const getValue = useCallback((obj, path) => {
    if (!path) return undefined;
    const parts = path.match(/([^[.\]])+/g);
    if (!parts) return undefined;
    return parts.reduce(
      (o, key) => (o && o[key] != null ? o[key] : undefined),
      obj
    );
  }, []);

  const sortedData = useMemo(() => {
    const safe = Array.isArray(data) ? data : [];
    if (!orderBy) return safe;

    const column = columns.find((col) => col.id === orderBy);
    const sortKey = sortBy || orderBy;

    const copy = safe.map((x) => x);

    copy.sort((a, b) => {
      const valueA = column?.sortFunction
        ? column.sortFunction(a)
        : getValue(a, sortKey);
      const valueB = column?.sortFunction
        ? column.sortFunction(b)
        : getValue(b, sortKey);

      if (valueA == null && valueB == null) return 0;
      if (valueA == null) return 1;
      if (valueB == null) return -1;

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return order === 'asc' ? valueA - valueB : valueB - valueA;
      }

      const strA = String(valueA);
      const strB = String(valueB);
      return order === 'asc'
        ? strA.localeCompare(strB)
        : strB.localeCompare(strA);
    });

    return copy;
  }, [data, columns, orderBy, sortBy, order, getValue]);

  const getDeviceInfo = () => {
    const userAgent = window.navigator.userAgent;
    const isMob = /Mobi|Android/i.test(userAgent);
    const isTablet = /Tablet|iPad/i.test(userAgent);
    const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
    const isAndroid = /Android/i.test(userAgent);
    return { isDevice: isMob || isTablet || isIOS || isAndroid };
  };

  const { isDevice } = getDeviceInfo();

  const pagedData = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return sortedData.slice(start, end);
  }, [sortedData, page, rowsPerPage]);

  const tableContent = useMemo(() => {
    const cellSx = bordered ? { border: '1px solid #e0e0e0' } : {};
    const headCellSx = bordered
      ? { border: '1px solid #e0e0e0', backgroundColor: '#f9fafb' }
      : {};

    return (
      <Table sx={bordered ? { borderCollapse: 'collapse' } : {}}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                sx={{ ...column.sx, ...headCellSx }}
                align={column.align || 'left'}
                sortDirection={orderBy === column.id ? order : false}
              >
                <TableSortLabel
                  active={orderBy === column.id}
                  direction={orderBy === column.id ? order : 'asc'}
                  onClick={() =>
                    handleRequestSort(
                      column.id,
                      column.sortBy ? column.sortBy : column.id
                    )
                  }
                >
                  {column.label}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        <TableBody>
          {pagedData.map((row, index) => (
            <TableRow
              key={row?.id ?? `${page}-${index}`}
              onClick={rowOnClick && isDevice ? () => rowOnClick(row) : null}
              style={rowStyles ? rowStyles(row) : {}}
              sx={
                rowOnClick && isDevice
                  ? {
                      ...hoverStyles,
                      '&:last-child td, &:last-child th': bordered
                        ? {}
                        : { border: 0 },
                    }
                  : {}
              }
              hover
              tabIndex={-1}
            >
              {columns.map((column) => {
                const value = row?.[column.id];

                if (column.id === 'no') {
                  return (
                    <TableCell key="no" align="left" sx={cellSx}>
                      {page * rowsPerPage + index + 1}
                    </TableCell>
                  );
                }

                if (column.id === 'checkbox') {
                  const statusNum = Number(row?.status);
                  const canShow = showCheckbox || statusNum === 5;

                  return (
                    <TableCell key="checkbox" align="left" sx={cellSx}>
                      {canShow && (
                        <Checkbox
                          checked={selected?.indexOf(row.id) !== -1}
                          onChange={(e) => handleCheckboxChange(e, row.id, row)}
                        />
                      )}
                    </TableCell>
                  );
                }

                return (
                  <TableCell
                    key={column.id}
                    align={column.align || 'left'}
                    sx={cellSx}
                  >
                    {column.format ? column.format(value, row) : value}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }, [
    columns,
    orderBy,
    order,
    handleRequestSort,
    pagedData,
    page,
    rowsPerPage,
    rowOnClick,
    isDevice,
    rowStyles,
    selected,
    handleCheckboxChange,
    showCheckbox,
    bordered,
  ]);

  return (
    <>
      {/* ✅ Top bar: responsive */}
      <Stack
        direction={isCompactHeader ? 'column' : 'row'}
        alignItems={isCompactHeader ? 'stretch' : 'center'}
        justifyContent="space-between"
        gap={isCompactHeader ? 1 : 2}
        paddingLeft={3}
        paddingRight={3}
        paddingTop={3}
      >
        {/* SOL: Filter + children */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          sx={{ minWidth: 0, flexWrap: 'nowrap' }}
        >
          {isMobile ? null : handleModal ? (
            <FilterViewButton variant="contained" onClick={handleModal} />
          ) : null}
          {children ? <div>{children}</div> : null}
          {leftHeaderSlot ? <div>{leftHeaderSlot}</div> : null}
        </Stack>

        {/* SAĞ: slot + pagination */}
        <Stack
          direction={isCompactHeader ? 'column' : 'row'}
          spacing={isCompactHeader ? 1 : 2}
          alignItems={isCompactHeader ? 'stretch' : 'center'}
          justifyContent="flex-end"
          sx={{
            ml: 'auto',
            width: { xs: '100%', md: 'auto' },
            flexWrap: 'nowrap',
          }}
        >
          {rightHeaderSlot ? (
            <div style={{ flex: 1, minWidth: 0, width: '100%' }}>
              {rightHeaderSlot}
            </div>
          ) : null}

          <TablePagination
            sx={{ flexShrink: 0 }}
            rowsPerPageOptions={rowsPerPageOptions}
            component="div"
            count={Array.isArray(data) ? data.length : 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={language.components.DynamicTable.text}
          />
        </Stack>
      </Stack>

      <div style={{ width: '100%', minWidth: 600 }}>
        <DoubleScrollbar>{tableContent}</DoubleScrollbar>
      </div>

      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={Array.isArray(data) ? data.length : 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={language.components.DynamicTable.text}
      />
    </>
  );
};

export default DynamicTable;
